import { UserTaskListFilterMetadataJsonType } from '@televet/shared-types/JsonTypes/UserTaskListFilterMetadataJsonType';
import _ from 'lodash';
import { TaskStatus, TaskType, TaskWhereInput } from 'shared/types/graphql';
import { ASSIGNED_TO_ME, COMPLETED_BY_ME, taskGroupsMap, UNASSIGNED } from '../constants';
import { TaskFilters, TaskList } from '../types';

export const buildTaskFilterFromClinicUser = (
  existingFilter: TaskFilters | undefined,
  completedTasksOnly: boolean,
  clinicUserId: string | undefined,
): TaskWhereInput => {
  const where: TaskWhereInput = {
    status: existingFilter?.status?.length
      ? {
          in: existingFilter.status.map(({ value }) => value as TaskStatus),
        }
      : undefined,
  };

  const assignees = existingFilter?.assignees;

  if (assignees?.length) {
    const assigneesIds: string[] = [];
    let includeUnassigned = false;

    for (const assignee of assignees) {
      if (assignee.value === ASSIGNED_TO_ME || assignee.value === COMPLETED_BY_ME) {
        assigneesIds.push(clinicUserId || '');
      } else if (assignee.value === UNASSIGNED) {
        includeUnassigned = true;
      } else {
        assigneesIds.push(assignee.value);
      }
    }

    if (completedTasksOnly) {
      where.completedByUserId = {
        in: assigneesIds,
      };
    } else {
      // Construct OR logic for assignees and unassigned
      where.AND = [
        {
          OR: [
            ...(assigneesIds.length > 0
              ? [
                  {
                    assignees: {
                      some: {
                        id: {
                          in: assigneesIds,
                        },
                      },
                    },
                  },
                ]
              : []),
            ...(includeUnassigned
              ? [
                  {
                    assignees: {
                      none: {},
                    },
                  },
                ]
              : []),
          ],
        },
      ];
    }
  }

  return where;
};

export const refineTaskFilterWithStatuses = (
  whereFilter: TaskWhereInput,
  taskStatuses: TaskStatus[],
  taskList: TaskList,
  currentClinicId: string | undefined,
  completedAt: Date | undefined,
): TaskWhereInput => {
  const clinicWidgetRequestIds: string[] = [];
  const where = _.clone(whereFilter);

  where.clinicId = {
    equals: currentClinicId,
  };

  if (!where.status) {
    where.status = {
      in: taskStatuses,
    };
  }

  if (completedAt) {
    where.completedAt = {
      gte: completedAt,
    };
  }

  const taskTypesInQuery = {
    [TaskType.WidgetRequest]: false,
    [TaskType.Automation]: false,
    [TaskType.Custom]: false,
  };

  for (const filter of taskList.taskFilters) {
    if (filter.taskType === TaskType.Automation) {
      taskTypesInQuery[TaskType.Automation] = true;
    } else if (filter.taskType === TaskType.Custom) {
      taskTypesInQuery[TaskType.Custom] = true;
    } else if (filter.taskType === TaskType.WidgetRequest) {
      taskTypesInQuery[TaskType.WidgetRequest] = true;
      const { entities } = filter.metadata as UserTaskListFilterMetadataJsonType;
      clinicWidgetRequestIds.push(...entities.map(({ entityId }) => entityId));
    }
  }

  const orConditions: TaskWhereInput[] = [];

  if (taskTypesInQuery[TaskType.Automation] || taskTypesInQuery[TaskType.Custom]) {
    orConditions.push({
      type: {
        equals: taskList.taskFilters[0].taskType,
      },
    });
  }

  if (taskTypesInQuery[TaskType.WidgetRequest]) {
    orConditions.push({
      AND: [
        { type: { equals: TaskType.WidgetRequest } },
        {
          widgetRequest: {
            clinicWidgetRequestType: {
              id: { in: clinicWidgetRequestIds },
            },
          },
        },
      ],
    });
  }

  where.OR = orConditions;

  return where;
};

export const findTabGroupNodeByIndex = (index: number): typeof taskGroupsMap[keyof typeof taskGroupsMap] => {
  for (const [key, { tabIndex }] of Object.entries(taskGroupsMap)) {
    if (tabIndex === index) {
      return taskGroupsMap[key];
    }
  }

  return taskGroupsMap.needsAttention;
};
